import {
  Color,
  ShowGeometryInstanceAttribute,
  createGuid,
  PolylineDashMaterialProperty,
  Event,
  Matrix4,
  Transforms,
  HeadingPitchRoll,
  CallbackProperty,
  Cartesian3,
  createPropertyDescriptor,
  defined,
  Quaternion,
  Matrix3,
  JulianDate,
  BoundingSphere,
} from "@cesium/engine";
import { Utils } from "@cesium/widgets";
import DitapViewer from "../basic/DitapViewer";
import EditMove from "./EditMove";

/**
 * 중심점 계산
 * @private
 * @param {Cartesian3[]} positions
 * @returns {Cartesian3}
 */
const getCenter = (positions) => {
  if (positions.length === 0) {
    return;
  }

  const minmax = Utils.computeMinMaxCartesian(positions);
  let center;
  if (minmax) {
    center = new Cartesian3(
      (minmax[0].x + minmax[1].x) / 2,
      (minmax[0].y + minmax[1].y) / 2,
      (minmax[0].z + minmax[1].z) / 2
    );
  }

  return center;
};

/**
 * position을 기준으로 translation, rotation, scale이 적용된 4x4 matrix 계산
 * @private
 * @param {Cartesian3} position 중심점
 * @param {Cartesian3} translation 이동량
 * @param {Quaternion | HeadingPitchRoll} rotation 회전량
 * @param {Cartesian3} scale 스케일
 * @returns {Matrix4} matrix 4x4 행렬
 */
const computeMatrix = (position, translation, rotation, scale) => {
  // HeadingPitchRoll  => Quaternion
  if (rotation instanceof HeadingPitchRoll) {
    rotation = Quaternion.fromHeadingPitchRoll(rotation, new Quaternion());
  }

  //# ENU -> ECEF convert value
  const toGlobal = Transforms.eastNorthUpToFixedFrame(position);
  const inverse = Matrix4.inverse(toGlobal, new Matrix4());

  //# combine matrix => scale x rotation
  const matrix = Matrix4.fromTranslationQuaternionRotationScale(
    Cartesian3.ZERO,
    rotation,
    scale,
    new Matrix4()
  );

  //# compute transform
  const transform = Matrix4.multiply(
    toGlobal,
    Matrix4.multiply(matrix, inverse, new Matrix4()),
    new Matrix4()
  );

  //# set translation
  Matrix4.setTranslation(
    transform,
    new Cartesian3(
      transform[12] + translation.x,
      transform[13] + translation.y,
      transform[14] + translation.z
    ),
    transform
  );

  return transform;
};

/**
 *
 * @private
 * @param {Cartesian3} origin
 * @param {Cartesian3} newPosition
 * @param {Quaternion} orientation Entity orientation
 * @param {Cartesian3} scale
 * @returns {Matrix4}
 */
const computePrimitiveMatrix = (origin, newPosition, orientation, scale) => {
  //polygonPrimitive.geometryInstances.geometry._polygonHierarchy.positions = newPositions;
  const matrix4 = Matrix4.fromRotationTranslation(
    Matrix3.fromQuaternion(orientation, new Matrix3()),
    newPosition,
    new Matrix4()
  );
  const originalEnu = Transforms.eastNorthUpToFixedFrame(newPosition); // 원본 중심정의 EastNorthUp 행렬 A
  const originalEnuInverse = Matrix4.inverse(originalEnu, new Matrix4()); // Inverse A^{-1}

  // A^{-1}BA => ENU 행렬 제거한 행렬 추출
  const matrix4_2 = Matrix4.multiply(
    originalEnuInverse,
    matrix4,
    new Matrix4()
  );

  // 회전행렬 추출
  const matrix3 = Matrix4.getRotation(matrix4_2, new Matrix3());

  const quaternion = Quaternion.fromRotationMatrix(matrix3, new Quaternion());

  const matrix = computeMatrix(
    origin,
    Cartesian3.subtract(newPosition, origin, new Cartesian3()),
    quaternion,
    scale
  );

  return matrix;
};

/**
 * Cartesian3 배열에 Matrix4 적용
 * @private
 * @param {Cartesian3[]} positions
 * @param {Matrix4} matrix
 * @param {Matrix4} inverse
 * @returns {Cartesian3[]}
 */
const setMatrixToPositionArray = (positions, matrix, inverse) => {
  const newPositions = [];

  // 회전행렬 적용
  for (const position of positions) {
    const newPosition = position.clone(new Cartesian3());

    // 역행렬
    if (defined(inverse)) {
      Matrix4.multiplyByPoint(inverse, newPosition, newPosition);
    }

    // 회전행렬
    Matrix4.multiplyByPoint(matrix, newPosition, newPosition);

    newPositions.push(newPosition);
  }

  return newPositions;
};

/**
 * Geometry Transform
 * @private
 * @param {Cartesian3[]} positions 좌표배열
 * @param {Cartesian3} origin 원본점
 * @param {Cartesian3} newPosition 이동점
 * @param {Quaternion} oldOrientaion 회전원본
 * @param {Quaternion} newOrientaion 적용회전
 * @param {Cartesian3} oldScale 비율원본
 * @param {Cartesian3} newScale 적용비율
 * @returns {Cartesian3[]} 변환좌표배열
 */
const transformGeometry = (
  positions,
  origin,
  newPosition,
  oldOrientaion,
  newOrientaion,
  oldScale,
  newScale
) => {
  // wall 변환행렬
  const matrix = Matrix4.fromTranslationQuaternionRotationScale(
    newPosition,
    newOrientaion,
    newScale,
    new Matrix4()
  );

  // wall 역행렬
  const inverse = Matrix4.inverse(
    Matrix4.fromTranslationQuaternionRotationScale(
      origin,
      oldOrientaion,
      oldScale,
      new Matrix4()
    ),
    new Matrix4()
  );

  const newPositions = setMatrixToPositionArray(positions, matrix, inverse);

  return newPositions;
};

/**
 * position 변경 핸들러
 * @param {object} parent
 * @param {*} newValue
 * @param {*} oldValue
 */
const positionChangedHandler = (parent) => {
  const pointEntities = parent.pointEntities;
  const polylineEntity = parent.polylineEntity;
  const polygonPrimitive = parent.polygonPrimitive;

  // polyline positions
  const positions = Utils.getPropertyValue(parent.positions);
  const origin = getCenter(positions);
  const position = Utils.getPropertyValue(parent.position);
  const orientationOffset = Transforms.headingPitchRollQuaternion(
    origin,
    new HeadingPitchRoll(0, 0, 0)
  );
  const orientation =
    Utils.getPropertyValue(parent.orientation) ||
    Transforms.headingPitchRollQuaternion(
      position,
      new HeadingPitchRoll(0, 0, 0)
    );
  const scaleOffset = Cartesian3.ONE;
  const scale = Utils.getPropertyValue(parent.scale) || Cartesian3.ONE;

  // CallbackProperty
  if (
    parent.position instanceof CallbackProperty ||
    parent.orientation instanceof CallbackProperty ||
    parent.scale instanceof CallbackProperty
  ) {
    // Point
    for (let i = 0; i < pointEntities.length; i++) {
      const pointPosition = positions[i];
      EditMove.setPosition(
        pointEntities[i],
        new CallbackProperty((time) => {
          const newPosition = Utils.getPropertyValue(parent.position, time);
          const newOrientation =
            Utils.getPropertyValue(parent.orientation, time) ||
            Transforms.headingPitchRollQuaternion(
              newPosition,
              new HeadingPitchRoll(0, 0, 0)
            );
          const newScale =
            Utils.getPropertyValue(parent.scale, time) || Cartesian3.ONE;
          const newPositions = transformGeometry(
            [pointPosition],
            origin,
            newPosition,
            orientationOffset,
            newOrientation,
            scaleOffset,
            newScale
          );

          return newPositions[0];
        }, false)
      );
    }

    // Polyline
    polylineEntity.polyline.positions = new CallbackProperty((time) => {
      const newPosition = Utils.getPropertyValue(parent.position, time);
      const newOrientation =
        Utils.getPropertyValue(parent.orientation, time) ||
        Transforms.headingPitchRollQuaternion(
          newPosition,
          new HeadingPitchRoll(0, 0, 0)
        );
      const newScale =
        Utils.getPropertyValue(parent.scale, time) || Cartesian3.ONE;
      const newPositions = transformGeometry(
        positions,
        origin,
        newPosition,
        orientationOffset,
        newOrientation,
        scaleOffset,
        newScale
      );

      polygonPrimitive.modelMatrix = computePrimitiveMatrix(
        origin,
        newPosition,
        newOrientation,
        newScale
      );

      return 2 < newPositions.length
        ? [...newPositions, newPositions[0]]
        : newPositions;
    }, false);

    // ConstantProperty
  } else {
    const newPolylinePositions = transformGeometry(
      positions,
      origin,
      position,
      orientationOffset,
      orientation,
      scaleOffset,
      scale
    );

    // Point
    for (let i = 0; i < pointEntities.length; i++) {
      const pointPosition = newPolylinePositions[i];
      pointEntities[i].position = pointPosition;
    }

    // Polyline
    polylineEntity.polyline.positions =
      2 < newPolylinePositions.length
        ? [...newPolylinePositions, newPolylinePositions[0]]
        : newPolylinePositions;

    // Polygon
    polygonPrimitive.modelMatrix = computePrimitiveMatrix(
      origin,
      position,
      orientation,
      scale
    );
  }
};

/**
 * positions 변경 핸들러
 * @param {object} parent
 * @param {*} newValue
 * @param {*} oldValue
 */
const positionsChangedHandler = (parent, positions) => {
  if (defined(parent.polygonEntity)) {
    // 초기화
    parent.reset();

    parent._positions = positions;

    // Entity 생성
    parent.createEntity();
  }
};

/**
 * orientation 변경 핸들러
 * @param {object} parent
 * @param {*} newValue
 * @param {*} oldValue
 */
const orientationChangedHandler = (parent) => {
  const pointEntities = parent.pointEntities;
  const polylineEntity = parent.polylineEntity;
  const polygonPrimitive = parent.polygonPrimitive;

  const positions = Utils.getPropertyValue(parent.positions);
  const origin = getCenter(positions);
  const position = Utils.getPropertyValue(parent.position);
  const orientationOffset = Transforms.headingPitchRollQuaternion(
    origin,
    new HeadingPitchRoll(0, 0, 0)
  );
  const orientation =
    Utils.getPropertyValue(parent.orientation) ||
    Transforms.headingPitchRollQuaternion(
      position,
      new HeadingPitchRoll(0, 0, 0)
    );
  const scaleOffset = Cartesian3.ONE;
  const scale = Utils.getPropertyValue(parent.scale) || Cartesian3.ONE;

  // CallbackProperty
  if (
    parent.position instanceof CallbackProperty ||
    parent.orientation instanceof CallbackProperty ||
    parent.scale instanceof CallbackProperty
  ) {
    // Point
    for (let i = 0; i < pointEntities.length; i++) {
      const pointPosition = positions[i];
      EditMove.setPosition(
        pointEntities[i],
        new CallbackProperty((time) => {
          const newPosition = Utils.getPropertyValue(parent.position, time);
          const newOrientation =
            Utils.getPropertyValue(parent.orientation, time) ||
            Transforms.headingPitchRollQuaternion(
              newPosition,
              new HeadingPitchRoll(0, 0, 0)
            );
          const newScale =
            Utils.getPropertyValue(parent.scale, time) || Cartesian3.ONE;
          const newPositions = transformGeometry(
            [pointPosition],
            origin,
            newPosition,
            orientationOffset,
            newOrientation,
            scaleOffset,
            newScale
          );

          return newPositions[0];
        }, false)
      );
    }

    // Polyline
    polylineEntity.polyline.positions = new CallbackProperty((time) => {
      const newPosition = Utils.getPropertyValue(parent.position, time);
      const newOrientation =
        Utils.getPropertyValue(parent.orientation, time) ||
        Transforms.headingPitchRollQuaternion(
          newPosition,
          new HeadingPitchRoll(0, 0, 0)
        );
      const newScale =
        Utils.getPropertyValue(parent.scale, time) || Cartesian3.ONE;
      const newPositions = transformGeometry(
        positions,
        origin,
        newPosition,
        orientationOffset,
        newOrientation,
        scaleOffset,
        newScale
      );

      // Polygon
      polygonPrimitive.modelMatrix = computePrimitiveMatrix(
        origin,
        newPosition,
        newOrientation,
        newScale
      );

      return 2 < newPositions.length
        ? [...newPositions, newPositions[0]]
        : newPositions;
    }, false);

    // ConstantProperty
  } else {
    const newPolylinePositions = transformGeometry(
      positions,
      origin,
      position,
      orientationOffset,
      orientation,
      scaleOffset,
      scale
    );

    // Point
    for (let i = 0; i < pointEntities.length; i++) {
      const pointPosition = newPolylinePositions[i];
      pointEntities[i].position = pointPosition;
    }

    // Polyline
    polylineEntity.polyline.positions =
      2 < newPolylinePositions.length
        ? [...newPolylinePositions, newPolylinePositions[0]]
        : newPolylinePositions;

    // Polygon
    polygonPrimitive.modelMatrix = computePrimitiveMatrix(
      origin,
      position,
      orientation,
      scale
    );
  }
};

/**
 * scale 변경 핸들러
 * @param {object} parent
 * @param {*} newValue
 * @param {*} oldValue
 */
const scaleChangedHandler = (parent) => {
  const pointEntities = parent.pointEntities;
  const polylineEntity = parent.polylineEntity;
  const polygonPrimitive = parent.polygonPrimitive;

  const positions = Utils.getPropertyValue(parent.positions);
  const origin = getCenter(positions);
  const position = Utils.getPropertyValue(parent.position);
  const orientationOffset = Transforms.headingPitchRollQuaternion(
    origin,
    new HeadingPitchRoll(0, 0, 0)
  );
  const orientation =
    Utils.getPropertyValue(parent.orientation) ||
    Transforms.headingPitchRollQuaternion(
      position,
      new HeadingPitchRoll(0, 0, 0)
    );
  const scaleOffset = Cartesian3.ONE;
  const scale = Utils.getPropertyValue(parent.scale) || Cartesian3.ONE;

  // CallbackProperty
  if (
    parent.position instanceof CallbackProperty ||
    parent.orientation instanceof CallbackProperty ||
    parent.scale instanceof CallbackProperty
  ) {
    // Point
    for (let i = 0; i < pointEntities.length; i++) {
      const pointPosition = positions[i];
      EditMove.setPosition(
        pointEntities[i],
        new CallbackProperty((time) => {
          const newPosition = Utils.getPropertyValue(parent.position, time);
          const newOrientation =
            Utils.getPropertyValue(parent.orientation, time) ||
            Transforms.headingPitchRollQuaternion(
              newPosition,
              new HeadingPitchRoll(0, 0, 0)
            );
          const newScale =
            Utils.getPropertyValue(parent.scale, time) || Cartesian3.ONE;
          const newPositions = transformGeometry(
            [pointPosition],
            origin,
            newPosition,
            orientationOffset,
            newOrientation,
            scaleOffset,
            newScale
          );

          return newPositions[0];
        }, false)
      );
    }

    // Polyline
    polylineEntity.polyline.positions = new CallbackProperty((time) => {
      const newPosition = Utils.getPropertyValue(parent.position, time);
      const newOrientation =
        Utils.getPropertyValue(parent.orientation, time) ||
        Transforms.headingPitchRollQuaternion(
          newPosition,
          new HeadingPitchRoll(0, 0, 0)
        );
      const newScale =
        Utils.getPropertyValue(parent.scale, time) || Cartesian3.ONE;
      const newPositions = transformGeometry(
        positions,
        origin,
        newPosition,
        orientationOffset,
        newOrientation,
        scaleOffset,
        newScale
      );

      // Polygon
      polygonPrimitive.modelMatrix = computePrimitiveMatrix(
        origin,
        newPosition,
        newOrientation,
        newScale
      );

      return 2 < newPositions.length
        ? [...newPositions, newPositions[0]]
        : newPositions;
    }, false);

    // ConstantProperty
  } else {
    const newPolylinePositions = transformGeometry(
      positions,
      origin,
      position,
      orientationOffset,
      orientation,
      scaleOffset,
      scale
    );

    // Point
    for (let i = 0; i < pointEntities.length; i++) {
      const pointPosition = newPolylinePositions[i];
      pointEntities[i].position = pointPosition;
    }

    // Polyline
    polylineEntity.polyline.positions =
      2 < newPolylinePositions.length
        ? [...newPolylinePositions, newPolylinePositions[0]]
        : newPolylinePositions;

    // Polygon
    polygonPrimitive.modelMatrix = computePrimitiveMatrix(
      origin,
      position,
      orientation,
      scale
    );
  }
};

/**
 * DefinitionChanged 이벤트 핸들러
 * @param {object} parent 부모 객체
 * @param {string} property 프로퍼티명
 * @param {*} newValue 변경값
 * @param {*} oldValue 이전값
 */
const definitionChangedHandler = (parent, property, newValue, oldValue) => {
  if (property === "position") {
    positionChangedHandler(parent);
  } else if (property === "positions") {
    positionsChangedHandler(parent, newValue);
  } else if (property === "orientation") {
    orientationChangedHandler(parent);
  } else if (property === "scale") {
    scaleChangedHandler(parent);
  }
};

/**
 * 면 엔티티 표시 클래스.</br>
 * Polygon entity display class.
 *
 * @alias PolygonEntity
 * @constructor
 *
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {Cartesian3[]} [positions] 면 생성을 위한 Cartesian 좌표 배열.</br> Cartesians for line creation.
 * @example
 * // 샌드캐슬 예제 'DiTAP - Polygon Entity' 참조
 *
 * const viewer = new DitapViewer("ditapContainer");
 *
 * // 1. 위치 지정 없이 객체 생성. (InteractionPolygonEntity를 통해 점 엔티티 표시.)
 * const PolygonEntity = new Ditap.PolygonEntity(viewer);
 *
 * const InteractionPolygonEntity = new Ditap.InteractionPolygonEntity();
 * InteractionPolygonEntity.PolygonEntity = PolygonEntity;
 *
 * // 2. 위치 지정하여 객체 생성. (InteractionPolygonEntity 통한 수정 불가.)
 * const position1 = new Ditap.Cartesian3.fromDegrees(
 *  126.9777639655642,
 *  37.56676148138595
 * );
 *
 * const position2 = new Ditap.Cartesian3.fromDegrees(
 *  126.97776396708979,
 *  37.56658104096736
 * );
 *
 * const position3 = new Ditap.Cartesian3.fromDegrees(
 *  126.97794279498576,
 *  37.566579036698215
 * );
 *
 * const position4 = new Ditap.Cartesian3.fromDegrees(
 *  126.9779494810781,
 *  37.56676163390728
 * );
 *
 * const PolygonEntity = new Ditap.PolygonEntity(viewer, [position1, position2, position3, position4]);
 *
 */
class PolygonEntity {
  constructor(viewer, positions) {
    this._id = `PolygonEntity${createGuid()}`;
    this._viewer = viewer;
    if (viewer instanceof DitapViewer) {
      this.viewer = viewer;
      viewer.ditapStorage.polygonEntity[this.id] = this;
    } else {
      console.error("Type doesn't match");
    }
    this._entityNum = 0;
    this._pointEntities = [];
    this._polylineEntity = undefined;
    this._polygonPrimitive = undefined;
    this._displayCondition = 2000;

    // if (positions) {
    //   this._positions = positions;
    //   this.createEntity();
    // } else {
    //   this._positions = [];
    // }
    this._position = undefined;
    this._positions = [];
    this._orientation = undefined;
    this._scale = undefined;

    // PointEntity Property 값 변경 감지 이벤트
    this._definitionChanged = new Event();

    // position, scale Property 정의
    Object.defineProperties(this, {
      /**
       * 선 엔티티 중심 좌표. </br>
       * LineEntity center coordinates.
       * @type {PositionProperty|undefined}
       */
      position: Utils.createPositionPropertyDescriptor("position"),

      /**
       * 선 엔티티 좌표 목록. </br>
       * LineEntity coordinate list.
       * @type {Property|Cartesian3[]}
       */
      positions: createPropertyDescriptor("positions"),

      /**
       * 선 엔티티 방향. </br>
       * LineEntity orientation.
       * @type {Property|undefined}
       */
      orientation: createPropertyDescriptor("orientation"),

      /**
       * 선 엔티티 비율. </br>
       * LineEntity Scale.
       * @type {PositionProperty|undefined}
       */
      scale: Utils.createPositionPropertyDescriptor("scale"),
    });

    // position, scale 값 변경 이벤트 핸들러 추가
    this._definitionChanged.addEventListener(definitionChangedHandler);

    // position 초기화
    if (positions) {
      this.positions = positions;
      this.createEntity();
    }
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  set viewer(viewer) {
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
    }
  }
  get viewer() {
    return this._viewer;
  }

  /*
   * 면 엔티티 ID.</br>
   * ID to assign to the PointEntity.
   */
  set id(id) {
    this._id = id;
  }
  get id() {
    return this._id;
  }

  /*
   * 엔티티 번호. </br>
   * Number of entities.
   * @type {number}
   */
  set entityNum(entityNum) {
    this._entityNum = entityNum;
  }
  get entityNum() {
    return this._entityNum;
  }

  /**
   * 면 생성 할 Cartesian 배열.</br>
   * Cartesian array for create PolygonEntity.
   * @type {Cartesian3[]}
   * @readonly
   */
  set positions(positions) {
    this._positions = positions;
  }
  get positions() {
    return this._positions;
  }

  /**
   * 면 생성으로 얻은 포인트 엔티티 배열.</br>
   * Array of point entities obtained by polygon creation.
   * @type {Entity[]}
   * @readonly
   */
  set pointEntities(pointEntities) {
    this._pointEntities = pointEntities;
  }
  get pointEntities() {
    return this._pointEntities;
  }

  /**
   * 면 생성으로 얻은 polyline Entity.</br>
   * Polyline Entity obtained by polygon creation.
   * @type {Entity}
   * @readonly
   */
  set polylineEntity(polylineEntity) {
    this._polylineEntity = polylineEntity;
  }
  get polylineEntity() {
    return this._polylineEntity;
  }

  /**
   * 면 생성으로 얻은 polygon primitive.</br>
   * polygon primitive obtained by polygon creation.
   * @type {Primitive}
   * @readonly
   */
  set polygonPrimitive(polygonPrimitive) {
    this._polygonPrimitive = polygonPrimitive;
  }
  get polygonPrimitive() {
    return this._polygonPrimitive;
  }

  /*
   * 엔티티의 DistanceDisplayCondition 값.</br>
   * DistanceDisplayCondition value of entity.
   * @type {number}
   */
  set displayCondition(displayCondition) {
    this._displayCondition = displayCondition;
  }
  get displayCondition() {
    return this._displayCondition;
  }

  /**
   * 인스턴스 설정 값 초기화. </br>
   * Initializing instance settings.
   */
  reset() {
    const viewer = this.viewer;
    for (const entity of this.pointEntities) {
      viewer.entities.remove(entity);
    }
    viewer.entities.remove(this.polylineEntity);
    viewer.scene.primitives.remove(this.polygonPrimitive);

    this._pointEntities = [];
    this._polylineEntity = undefined;
    this._polygonPrimitive = undefined;
    this._entityNum = 0;
    this._displayCondition = 2000;
    this._depthFailAlpha = 0.3;
    this._positions = [];
    this._position = undefined;
    this._orientation = undefined;
    this._scale = undefined;
  }

  /*
   * Ditap Viewer에 면 엔티티 생성.</br>
   * Create a PolygonEntity in the DitapViewer.
   */
  createEntity() {
    const positions = Utils.getPropertyValue(this.positions);
    const displayCondition = Utils.getDistanceDisplayConditon(positions);

    if (displayCondition > 2000) {
      this.displayCondition = displayCondition;
    }

    positions.forEach((position) => {
      const point = Utils.createPoint({
        viewer: this.viewer,
        id: this.id + this.entityNum,
        position: position,
        outlineColor: Color.fromBytes(13, 94, 238, 255),
        displayCondition: this.displayCondition,
      });
      this.entityNum++;
      this.pointEntities.push(point);
    });

    const polygonLine = Utils.createPolyline({
      viewer: this.viewer,
      id: this.id + this.entityNum,
      positions: [...positions, positions[0]],
      material: Color.fromBytes(13, 94, 238, 255),
      displayCondition: this.displayCondition,
    });
    this.entityNum++;

    const polygonPolygon = Utils.createPolygonPrimitive({
      viewer: this.viewer,
      id: this.id + this.entityNum,
      positions: positions,
      material: Color.fromBytes(13, 94, 238, 51),
      displayCondition: this.displayCondition,
    });
    this.entityNum++;

    this.polylineEntity = polygonLine;
    this.polygonPrimitive = polygonPolygon;

    this._position = polygonLine.position;
  }

  /*
   * Ditap Viewer에서 point Entity 제거.</br>
   * Remove pointEntity in the ditapViewer.
   * @param {Entity} entity 제거될 point Entity.</br>The point Entity to be removed.
   */
  removePointEntity(entity) {
    const findEntity = (findentity) => findentity === entity;
    const index = this.pointEntities.findIndex(findEntity);
    this.pointEntities.splice(index, index + 1);
    this.viewer.entities.remove(entity);
  }

  /*
   * Ditap Viewer에서 polyline Entity 제거.</br>
   * Remove the polylineEntity in the ditapViewer.
   */
  removePolylineEntity() {
    this.viewer.entities.remove(this.polylineEntity);
    this.polylineEntity = undefined;
  }

  /*
   * Ditap Viewer에서 polygon primitive 제거.</br>
   * Remove the polygonPrimitive in the ditapViewer.
   */
  removePolygonPrimitive() {
    this.viewer.scene.primitives.remove(this.polygonPrimitive);
    this.polygonPrimitive = undefined;
  }

  /*
   * 저장된 좌표 배열에서 삭제할 좌표.
   * The position to be removed from the saved coordinate array
   * @param {Cartesian3} position 삭제될 좌표.</br>The position to delete.
   */
  removePositions(position) {
    const findPosition = (findposition) => findposition === position;
    const positions = Utils.getPropertyValue(this.positions);
    const index = positions.findIndex(findPosition);
    positions.splice(index, index + 1);
    this.positions = positions;
  }

  /**
   * 인스턴스 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the instance. Should be called if permanently removing the instance from layout.
   */
  destroy() {
    this.reset();
    this.viewer.ditapStorage.removeInstanceById(this.id);
  }

  /*
   * 생성된 엔티티 색상 변경.</br>
   * Change the color of the generated entity
   * @param {Entity} entity 변경 할 엔티티.</br> Entities to change.
   * @param {Color} color 변경 할 색상.</br> Color to change.
   */
  setEntityColor(entity, color) {
    if (entity.point) {
      entity.point.color = color;
    } else if (entity.polyline) {
      entity.polyline.material = color;
    } else if (entity.polygon) {
      entity.polygon.material = color;
    }
  }

  /*
   * 모든 엔티티 표시 여부 변경.</br>
   * Sets all entities in PolygonEntity shold be displayed.
   * @param {boolean} flag 표시 여부 값.</br> The value of show.
   */
  setAllEntitiesShow(flag) {
    this.setAllPointEntitiesShow(flag);
    this.setPolylineEntitiesShow(flag);
    this.setpolygonPrimitiveEntitiesShow(flag);
  }

  /*
   * 모든 점 엔티티 표시 여부 변경.</br>
   * Sets all entities in Point Entity shold be displayed.
   * @param {boolean} flag 표시 여부 값.</br> The value of show.
   */
  setAllPointEntitiesShow(flag) {
    this.pointEntities.forEach((point) => {
      point.point.show = flag;
    });
  }

  /*
   * 선 엔티티 표시 여부 변경.</br>
   * Sets all entities in PolygonEntity shold be displayed.
   * @param {boolean} flag 표시 여부 값.</br> The value of show.
   */
  setPolylineEntitiesShow(flag) {
    this.polylineEntity.polyline.show = flag;
  }

  /*
   * 면 primitive 표시 여부 변경.</br>
   * Sets all entities in Polygon Primitive shold be displayed.
   * @param {boolean} flag 표시 여부 값.</br> The value of show.
   */
  setpolygonPrimitiveEntitiesShow(flag) {
    // this.polygonPrimitive.polygon.show = flag;
    const id = this.polygonPrimitive.geometryInstances.id;
    const attributes = this.polygonPrimitive.getGeometryInstanceAttributes(id);
    attributes.show = ShowGeometryInstanceAttribute.toValue(flag);
  }

  /*
   * 면 primitive 재질 변경.</br>
   * Set the material of polygonPrimitive.
   * @param {MaterialProperty} material 변경할 재질.</br> The material to change.
   */
  setPolygonMaterial(material) {
    this.polygonPrimitive.appearance.material = material;
  }

  /*
   * 선 엔티티 재질 변경.</br>
   * Set the material of polylineEntity.
   * @param {MaterialProperty} material 변경할 재질.</br> The material to change.
   */
  setPolylineMaterial(material) {
    this.polylineEntity.polyline.material = material;
  }

  /*
   * 선 엔티티 depthFailMaterial 변경.</br>
   * Set the depthFailMaterial of polylineEntity.
   * @param {MaterialProperty} depthFailMaterial 변경할 depthFailMateria.</br> The depthFailMaterial to change.
   */
  setPolylineDepthFailMaterial(material) {
    this.polylineEntity.polyline.material = material;

    this.polylineEntity.polyline.depthFailMaterial = new PolylineDashMaterialProperty(
      {
        color: material.withAlpha(0.5),
        dashLength: 10,
      }
    );
  }

  /*
   * 면 primitive depthFailMaterial 변경.</br>
   * Set the depthFailMaterial of polygon Pprimitive.
   * @param {MaterialProperty} depthFailMaterial 변경할 depthFailMateria.</br> The depthFailMaterial to change
   */
  setPolygonDepthFailMaterial(depthFailMaterial) {
    this.polygonPrimitive.depthFailAppearance.material = depthFailMaterial;
  }

  /*
   * 선 엔티티 너비 변경.</br>
   * Set the width of polylineEntity.
   * @param {number} width 변경할 너비 값.</br> The width to change.
   */
  setPolylineWidth(width) {
    this.polylineEntity.polyline.width = Number(width);
  }

  /*
   * 점 엔티티 윤곽선 색상 변경.</br>
   * Set the outline color of pointEntity.
   * @param {Entity} pointEntity 색상을 변경할 엔티티.</br> The Entity to change color.
   * @param {Color} color 변경할 색상.</br> The color to change.
   */
  setPointOutlineColor(pointEntity, color) {
    pointEntity.point.outlineColor = color;
  }

  /*
   * 모든 점 엔티티 윤곽선 색상 변경.</br>
   * Sets all outline color of pointEntities in LinestringEntity.
   * @param {Color} color 변경할 색상.</br> The color to change.
   */
  setAllPointsOutlineColor(color) {
    this.pointEntities.forEach((pointEntity) => {
      this.setPointOutlineColor(pointEntity, color);
    });
  }

  /*
   * 모든 포인트 엔티티 픽셀 사이즈 변경.</br>
   * Sets all pixel size of pointEntities in LinestringEntity.
   * @param {number} pixelSize 변경할 픽셀 사이즈.</br> The pixel size to change.
   */
  setAllPointsPixelSize(pixelSize) {
    this.pointEntities.forEach((pointEntity) => {
      this.setPointPixelSize(pointEntity, pixelSize);
    });
  }

  /*
   * 포인트 엔티티 픽셀 사이즈 변경.</br>
   * Set pixel size of pointEntitiy.
   * @param {Entity} pointEntity 픽셀 사이즈 변경할 포인트 엔티티.</br> The pointEntity to change pixelSize.
   * @param {number} size 변경할 픽셀 사이즈.</br> The pixel size to change.
   */
  setPointPixelSize(spointEntity, size) {
    spointEntity.point.pixelSize = size;
  }

  /**
   * 지정된 시간에 엔티티에 대한 positions 계산. <br>
   * Calculate positions for an entity at a given time.
   * @param {JulianDate} [time=JulianDate] time 모델 positions를 검색하는 시간. <br> The time to retrieve model positions for.
   * @returns {positions | undefined} Model positions. <br> Model positions.
   */
  computePositions(time = new JulianDate()) {
    const position = Utils.getPropertyValue(this.position, time);
    const positions = Utils.getPropertyValue(this.positions, time);
    if (!(position && positions && 0 < positions.length)) {
      return;
    }

    const center = getCenter(positions);

    const matrix = this.computeModelMatrix();
    if (matrix) {
      const inverse = Matrix4.inverse(
        Matrix4.fromTranslationQuaternionRotationScale(
          center,
          Quaternion.IDENTITY,
          Cartesian3.ONE
        ),
        new Matrix4()
      );

      const newPositions = setMatrixToPositionArray(positions, matrix, inverse);
      return newPositions;
    }
  }

  /**
   * 지정된 시간에 엔티티에 대한 BoundingSphere 계산. <br>
   * Calculate BoundingSphere for an entity at a given time.
   * @param {JulianDate} [time=JulianDate] time 모델 BoundingSphere를 검색하는 시간. <br> The time to retrieve model BoundingSphere for.
   * @returns {BoundingSphere | undefined} Model BoundingSphere. <br> Model BoundingSphere.
   */
  computeBoundingSphere(time = new JulianDate()) {
    const position = Utils.getPropertyValue(this.position, time);
    const positions = Utils.getPropertyValue(this.positions, time);
    if (!(position && positions && 0 < positions.length)) {
      return;
    }

    let center = getCenter(positions);
    let minmax = Utils.computeMinMaxCartesian(positions);

    const matrix = this.computeModelMatrix();
    if (matrix) {
      const inverse = Matrix4.inverse(
        Matrix4.fromTranslationQuaternionRotationScale(
          center,
          Quaternion.IDENTITY,
          Cartesian3.ONE
        ),
        new Matrix4()
      );

      const newPositions = setMatrixToPositionArray(positions, matrix, inverse);

      center = getCenter(newPositions);
      minmax = Utils.computeMinMaxCartesian(newPositions);
    }

    // BoundingSphere 반지름
    const radius = Math.sqrt(
      (Math.pow(minmax[1].x - minmax[0].x, 2) +
        Math.pow(minmax[1].y - minmax[0].y, 2) +
        Math.pow(minmax[1].z - minmax[0].z, 2)) /
        2
    );

    const boundingSphere = new BoundingSphere(center, radius);

    return boundingSphere;
  }

  /**
   * 지정된 시간에 엔티티 변환에 대한 모델 행렬 계산. <br>
   * Compute model matrix for entity transformation at specified time.
   * @param {JulianDate} [time=JulianDate] time 모델 행렬을 검색하는 시간. <br> The time to retrieve model matrix for.
   * @returns {Matrix4 | undefined} 모델 행렬. <br> Model matrix.
   */
  computeModelMatrix(time = new JulianDate()) {
    const position = Utils.getPropertyValue(this.position, time);
    if (!defined(position)) {
      return;
    }

    const orientation =
      Utils.getPropertyValue(this.orientation, time) ||
      Transforms.headingPitchRollQuaternion(
        position,
        new HeadingPitchRoll(0, 0, 0)
      );
    const scale = Utils.getPropertyValue(this.scale, time) || Cartesian3.ONE;

    const matrix = Matrix4.fromTranslationQuaternionRotationScale(
      position,
      orientation,
      scale,
      new Matrix4()
    );

    return matrix;
  }
}
export default PolygonEntity;
